import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import { getBusinessUnits } from "@/api/chat";
import { QUERY_KEYS, STALE } from "@/lib/query-constants";

/**
 * Canonical hook for fetching business units.
 * Syncs the result into the global business-unit store consumed across the app
 * and ensures a valid default business unit is always selected.
 */
export function useAllBusinessUnits() {
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.user?.role);
  // Supplier accounts (role 5) work against customer allocations, not an own
  // facility catalogue. The organisation business-units endpoint rejects them.
  const canLoadBusinessUnits = !!token && role != null && role !== 5;
  const setBusinessUnits = useBusinessUnitStore((s) => s.setBusinessUnits);
  const setCurrentBusinessUnit = useBusinessUnitStore(
    (s) => s.setCurrentBusinessUnit,
  );
  const currentBusinessUnit = useBusinessUnitStore(
    (s) => s.currentBusinessUnit,
  );

  const query = useQuery({
    queryKey: QUERY_KEYS.businessUnits,
    queryFn: () =>
      canLoadBusinessUnits ? getBusinessUnits(token!) : Promise.resolve([]),
    enabled: canLoadBusinessUnits,
    staleTime: STALE.THIRTY_MIN,
  });

  useEffect(() => {
    if (canLoadBusinessUnits && query.data) {
      setBusinessUnits(query.data);

      /* Compare as strings: the API returns `id` as a number even though it is
         typed as `string`, so a strict equality check never matched and this
         effect reset the persisted selection back to the first facility. */
      const currentStillValid = query.data.some(
        (bu) => String(bu.id) === String(currentBusinessUnit),
      );
      if (!currentBusinessUnit || !currentStillValid) {
        setCurrentBusinessUnit(
          query.data.length ? String(query.data[0].id) : null,
        );
      }
    }
  }, [
    query.data,
    canLoadBusinessUnits,
    currentBusinessUnit,
    setBusinessUnits,
    setCurrentBusinessUnit,
  ]);

  return query;
}
